import React, { CSSProperties, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import Button from '@components/ui/Button';
import Heading from '@components/ui/Heading';
import Container from '@components/global/Container';

import styles from './ProductGrid.module.scss';
import MarkdownCopy from '../Slices/MarkdownCopy';
import ToolTipsOverlay, { ITooltip } from './ToolTipsOverlay';
import generateToolTipsFromImage from './Transforms/tooltips';
import PrismicImage from '../PrismicImage';
import useObjectVisibility from '@hooks/useObjectVisibility';
import {
  InteractionLocation,
  ProductGridEventProps,
  PromotionEventName,
  trackPromotionEvent,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

export interface ProductGridImage {
  product_image: {
    dimensions?: {
      width: number;
      height: number;
    };
    alt?: string | null;
    url?: string | null;
    tooltips?: ITooltip[];
  };
  tooltip1_title?: any;
  tooltip1_body?: any;
  tooltip1_x: number | null;
  tooltip1_y: number | null;
  tooltip2_title?: any;
  tooltip2_body?: any;
  tooltip2_x: number | null;
  tooltip2_y: number | null;
  tooltip3_title?: any;
  tooltip3_body?: any;
  tooltip3_x: number | null;
  tooltip3_y: number | null;
}

export interface ProductGridProps {
  title?: string | null;
  id?: string;
  body?: string;
  images?: ProductGridImage[];
  cta: {
    text: string | null;
    url: string | null;
    importance: 'primary' | 'secondary' | 'tertiary';
  };
  altPosition?: boolean;
  icon?:
    | {
        dimensions: {
          width: number;
          height: number;
        };
        alt: string | null;
        url: string | null;
      }
    | Record<string, never>;
  socialProof?: {
    top?: string;
    bottom?: string;
    topSize: 'small' | 'large';
    bottomSize: 'small' | 'large';
  };
  debug?: boolean;
  brandedStoreCoopAttribute?: string | null;
  sliceIndex?: number;
}

const ProductGrid = ({
  title,
  id,
  body,
  images,
  cta,
  altPosition,
  icon,
  socialProof,
  brandedStoreCoopAttribute,
  sliceIndex,
  debug = false,
}: ProductGridProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isButtonVisible = useObjectVisibility(buttonRef);

  const buttonPromotionTracking = useMemo(
    () => ({
      page: {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute || null,
        interaction_location: InteractionLocation.ProductGrid,
      },
      ecommerce: {
        creative_name: `link,${cta?.url}`,
        creative_slot: buildCreativeSlotName({
          sliceIndex,
          interactionLocation: InteractionLocation.ProductGrid,
        }),
        promotion_id: title || null,
        promotion_name: cta?.text || null,
        isVideo: false,
      },
    }),
    [brandedStoreCoopAttribute, cta?.text, cta?.url, sliceIndex, title]
  );

  useEffect(() => {
    if (isButtonVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...buttonPromotionTracking,
      } as ProductGridEventProps);
    }
  }, [isButtonVisible, buttonPromotionTracking]);

  if (!title || !images?.[0].product_image.url) {
    return null;
  }
  const cssImageWidth = {
    '--image-width': `${icon?.dimensions?.width ?? 40}px`,
  } as CSSProperties;

  return (
    <Container>
      <section
        id={id}
        className={cn(styles.productGrid, {
          [styles.longGrid]: images?.length > 3,
          [styles.altGrid]: altPosition,
        })}
      >
        <div className={styles.content}>
          {title && (
            <Heading
              semanticLevel="h2"
              styleLevel="2"
              className={styles.heading}
            >
              {title}
            </Heading>
          )}
          {body && <MarkdownCopy text={body ?? ''} className={styles.body} />}

          <div className={styles.ratings} style={cssImageWidth}>
            {icon?.url && icon?.dimensions.height && icon?.dimensions.width && (
              <PrismicImage
                src={icon.url}
                layout="fixed"
                objectFit="contain"
                height={icon.dimensions.height}
                width={icon.dimensions.width}
                alt={icon.alt ?? ''}
              />
            )}
            <div>
              {socialProof?.top && (
                <div
                  className={cn({
                    [styles.textSmall]: socialProof.topSize === 'small',
                    [styles.textLarge]: socialProof.topSize === 'large',
                  })}
                >
                  <MarkdownCopy text={socialProof.top} />
                </div>
              )}
              {socialProof?.bottom && (
                <div
                  className={cn({
                    [styles.textSmall]: socialProof.bottomSize === 'small',
                    [styles.textLarge]: socialProof.bottomSize === 'large',
                  })}
                >
                  <MarkdownCopy text={socialProof.bottom} />
                </div>
              )}
            </div>
          </div>
          {cta?.text && cta?.url && (
            <Button
              label={cta.text}
              href={cta.url}
              size="large"
              variant={
                ['primary', 'secondary', 'tertiary'].includes(cta.importance)
                  ? cta.importance
                  : 'primary'
              }
              ref={buttonRef}
              handleClick={() => {
                trackPromotionEvent({
                  eventName: PromotionEventName.selectPromotion,
                  ...buttonPromotionTracking,
                } as ProductGridEventProps);
              }}
            />
          )}
        </div>

        <div
          className={cn(styles.imageGrid, {
            [styles.grid2]: images.length === 2,
            [styles.grid3]: images.length >= 3,
          })}
        >
          {images?.map((item: ProductGridImage) => {
            const tooltips = generateToolTipsFromImage(item);
            return item?.product_image?.url ? (
              <div
                className={cn([
                  styles.imageContainer,
                  { [styles.debugPosition]: debug },
                ])}
                key={item?.product_image.url}
              >
                <ToolTipsOverlay tooltips={tooltips}>
                  <PrismicImage
                    src={item?.product_image?.url}
                    layout="fill"
                    height={750}
                    width={650}
                    objectPosition="center"
                    alt={item?.product_image.alt ?? ''}
                  />
                </ToolTipsOverlay>
              </div>
            ) : null;
          })}
        </div>
      </section>
    </Container>
  );
};

export default ProductGrid;
